import React, { useCallback, useEffect, useState } from "react";
import { Button, Group, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import AccountingReportFilters from "./accounting/AccountingReportFilters";
import JournalRegisterView from "./accounting/JournalRegisterView";
import {
  downloadJournalRegisterCsv,
  flattenJournalEntries,
} from "./accounting/journalRegisterUtils";

const ReportJournalRegister = () => {
  const [api] = useServerApi();
  const { t, i18n } = useTranslation();
  const [fiscalPeriodId, setFiscalPeriodId] = useState(null);
  const [periodName, setPeriodName] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const loadReport = async () => {
    if (!fiscalPeriodId) return;
    setLoading(true);
    try {
      const result = await api.search({
        apiEntity: "journalEntry",
        pageSize: 500,
        currentPage: 1,
        sort: "entryDate",
        order: "asc",
        query: {
          status: "POSTED",
          fiscalPeriod: fiscalPeriodId,
        },
      });
      const docs = result?.docs || [];
      setRows(flattenJournalEntries(docs));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fiscalPeriodId) return;
    const loadPeriodName = async () => {
      try {
        const period = await api.getById({
          apiEntity: "fiscalPeriod",
          id: fiscalPeriodId,
        });
        setPeriodName(period?.name || "");
      } catch (error) {
        setPeriodName("");
      }
    };
    loadPeriodName();
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalPeriodId]);

  const handleDownload = useCallback(() => {
    if (rows.length === 0) {
      showNotification({
        color: "yellow",
        title: t("accounting.trialBalance.downloadFailed"),
        message: t("accounting.financeReports.journalRegister.noActivity"),
      });
      return;
    }
    downloadJournalRegisterCsv({ rows, periodName, t });
  }, [periodName, rows, t]);

  return (
    <div>
      <Text size="xs" color="dimmed" mb="xs">
        {t("accounting.financeReports.journalRegister.intro")}
      </Text>
      <AccountingReportFilters
        fiscalPeriodId={fiscalPeriodId}
        onFiscalPeriodChange={setFiscalPeriodId}
      />
      <Group mb="sm">
        <Button
          size="xs"
          variant="default"
          onClick={handleDownload}
          disabled={rows.length === 0}
        >
          {t("Download")}
        </Button>
        <Button size="xs" onClick={loadReport} loading={loading}>
          {t("Refresh")}
        </Button>
      </Group>
      <JournalRegisterView
        rows={rows}
        loading={loading}
        i18nLanguage={i18n.language}
      />
    </div>
  );
};

export default ReportJournalRegister;
